/*
 * Opus cart drawer — Prestige 7.3.1 edition (lumoira.co)
 * ---------------------------------------------------------------------------
 * Adds to the theme's own <cart-drawer> web component the pieces Liquid
 * cannot render on its own, without touching theme.js:
 *
 *   - the AI upsell carousel, fed by Shopify's Product Recommendations API
 *     for every product in the cart (sections/cart-drawer-upsells.liquid),
 *     merged, de-duplicated and cached so it repaints
 *     instantly after every cart change
 *   - the tier bar fill animating from its previous width to the new one
 *   - a loading state: progress line under the header, dimmed card while the
 *     theme talks to the cart, with a watchdog so nothing can stay stuck
 *   - keyboard focus and the order note surviving the theme's re-render,
 *     and a polite live region so screen readers hear each update
 *   - inline errors for the upsell "add" buttons
 *   - region gating for the reassurance line when Liquid could not decide
 *
 * The theme re-renders the entire drawer from server HTML on every cart
 * change (cart:change → CartDrawer#replaceContent), so state is read back
 * from [data-cvd-state] inside the drawer after each render, never fetched
 * from /cart.js and never recomputed here.
 *
 * Theme events used: cart:change, line-item:will-change, cart:error,
 * theme:loading:start / theme:loading:end, dialog:before-show,
 * shopify:section:load.
 */

(function () {
  'use strict';

  if (window.__cvdOpusDrawer) return;
  window.__cvdOpusDrawer = true;

  var SEL = {
    drawer: '#cart-drawer',
    state: '[data-cvd-state]',
    live: '[data-cvd-live]',
    item: '[data-cvd-item]',
    tiers: '[data-cvd-tiers]',
    tiersFill: '[data-cvd-tiers-fill]',
    tier: '[data-cvd-tier]',
    upsell: '[data-cvd-upsell]',
    slider: '[data-cvd-slider]',
    sliderViewport: '[data-cvd-slider-viewport]',
    sliderTrack: '[data-cvd-slider-track]',
    sliderPrev: '[data-cvd-slider-prev]',
    sliderNext: '[data-cvd-slider-next]',
    sliderDots: '[data-cvd-slider-dots]',
    slides: '[data-cvd-slides]',
    slide: '[data-cvd-slide]',
    variantSelect: '[data-cvd-variant-select]',
    upsellPrice: '[data-cvd-upsell-price]',
    upsellCompare: '[data-cvd-upsell-compare]',
    upsellError: '[data-cvd-upsell-error]',
    notes: '.cvd-notes',
    geo: '[data-cvd-geo]'
  };

  var CLASS_LOADING = 'is-loading';
  var CLASS_NEW = 'is-new';

  /* Longer than any cart request should take, short enough not to look broken. */
  var LOADING_TIMEOUT = 8000;
  /* A successful change re-renders well inside this; a failed one never does. */
  var ITEM_LOADING_GRACE = 250;

  var GEO_URL = '/browsing_context_suggestions.json';
  var GEO_CACHE_KEY = 'cvd-geo-country';

  var drawer = null;
  var observer = null;
  var initQueued = false;
  var opened = false;
  var rendered = false;

  var lastFill = null;
  var lastReached = null;

  var slider = null;
  var sliderIndex = 0;
  var upsellSeed = null;
  var upsellProducts = null;
  var upsellCache = null;
  var upsellRequest = 0;

  var loadingTimer = null;
  var itemTimer = null;
  var geoRequest = null;
  var geoFailed = false;

  var pendingFocus = null;
  var keyboardUser = false;
  /* cart requests currently running: a keyboard user can start a second one
     before the first re-render, and the focus target must outlive both */
  var inflight = 0;

  var noteOpen = null;
  var noteValue = null;

  /* ----------------------------------------------------------------- util */

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function isRtl(node) {
    return getComputedStyle(node).direction === 'rtl';
  }

  function readState(scope) {
    var node = $(SEL.state, scope);
    if (!node) return null;

    return {
      count: parseInt(node.getAttribute('data-cvd-count'), 10) || 0,
      products: (node.getAttribute('data-cvd-products') || '').split(',').filter(Boolean),
      recommend: node.getAttribute('data-cvd-recommend') || '',
      seeds: (node.getAttribute('data-cvd-recommend') || '').split(',').filter(Boolean),
      upsells: node.getAttribute('data-cvd-upsells') === 'true',
      limit: parseInt(node.getAttribute('data-cvd-upsell-limit'), 10) || 8,
      url: node.getAttribute('data-cvd-upsell-url') || '/recommendations/products',
      intent: node.getAttribute('data-cvd-upsell-intent') || 'related',
      fallback: node.getAttribute('data-cvd-upsell-fallback') || '',
      updating: node.getAttribute('data-cvd-updating') || ''
    };
  }

  /* ------------------------------------------------------------- tier bar */

  /**
   * Liquid renders the fill at its final width. To make it travel there, it
   * is set back to the width it had before this render (no transition), then
   * released to the new one on the next frame. Milestones that just became
   * reached get a one-off pop.
   */
  function syncTiers(scope) {
    var tiers = $(SEL.tiers, scope);
    if (!tiers) {
      lastFill = null;
      lastReached = null;
      return;
    }

    var fill = $(SEL.tiersFill, tiers);
    var reached = parseInt(tiers.getAttribute('data-cvd-tiers-reached'), 10) || 0;

    if (fill) {
      var target = fill.style.width;

      if (lastFill !== null && lastFill !== target) {
        fill.style.transition = 'none';
        fill.style.width = lastFill;
        fill.offsetHeight; // reflow so the change below animates
        fill.style.transition = '';
        requestAnimationFrame(function () {
          fill.style.width = target;
        });
      }

      lastFill = target;
    }

    if (lastReached !== null && reached > lastReached) {
      $$(SEL.tier, tiers).forEach(function (step, index) {
        if (index >= lastReached && index < reached) step.classList.add(CLASS_NEW);
      });
    }

    lastReached = reached;
  }

  /* -------------------------------------------------------------- loading */

  /**
   * A line card is dimmed while its request runs. The theme's
   * LineItemQuantity never re-renders after a failed /cart/change.js (a 422,
   * or a network error), so the card would stay dimmed forever: undo it
   * shortly after the theme reports the request finished. A successful
   * change replaces the nodes before the grace period ends.
   */
  function clearItemLoading() {
    if (itemTimer) {
      clearTimeout(itemTimer);
      itemTimer = null;
    }
    if (!drawer) return;

    var items = $$(SEL.item + '.' + CLASS_LOADING, drawer);

    // a change that never re-rendered leaves a focus target nobody consumes;
    // drop it so a later, unrelated render does not pull focus into that line
    if (items.length && !inflight) pendingFocus = null;

    items.forEach(function (item) {
      item.classList.remove(CLASS_LOADING);
      var field = item.querySelector('[data-line-key]');
      if (field) field.value = field.defaultValue;
    });
  }

  function clearLoading() {
    if (loadingTimer) {
      clearTimeout(loadingTimer);
      loadingTimer = null;
    }
    if (drawer) drawer.classList.remove(CLASS_LOADING);
    inflight = 0;
    clearItemLoading();
  }

  function onLoadingStart() {
    if (!drawer) return;
    inflight += 1;
    drawer.classList.add(CLASS_LOADING);
    announce(readState(drawer) && readState(drawer).updating);

    if (loadingTimer) clearTimeout(loadingTimer);
    loadingTimer = setTimeout(clearLoading, LOADING_TIMEOUT);
  }

  function onLoadingEnd() {
    inflight = Math.max(0, inflight - 1);
    if (loadingTimer) {
      clearTimeout(loadingTimer);
      loadingTimer = null;
    }
    if (drawer) drawer.classList.remove(CLASS_LOADING);

    if (itemTimer) clearTimeout(itemTimer);
    itemTimer = setTimeout(clearItemLoading, ITEM_LOADING_GRACE);
  }

  /* ----------------------------------------------------------------- a11y */

  function announce(text) {
    if (!drawer || !text) return;
    var live = $(SEL.live, drawer);
    if (!live) return;

    // clearing first makes an identical message read again
    live.textContent = '';
    setTimeout(function () {
      live.textContent = text;
    }, 50);
  }

  function announceRender() {
    if (!drawer) return;
    var title = $('.cvd-head__title', drawer);
    var total = $('.cvd-checkout__total', drawer);
    var tier = $('.cvd-tiers__text', drawer);
    var parts = [];

    if (title) parts.push(title.textContent.replace(/\s+/g, ' ').trim());
    if (total) parts.push(total.textContent.trim());
    if (tier) parts.push(tier.textContent.replace(/\s+/g, ' ').trim());

    announce(parts.join('. '));
  }

  // Only keyboard and screen-reader users need focus put back; pointer users
  // never notice it, and a programmatic focus() on the quantity <input>
  // would light it up on touch.
  document.addEventListener(
    'keydown',
    function () {
      keyboardUser = true;
    },
    true
  );
  document.addEventListener(
    'pointerdown',
    function () {
      keyboardUser = false;
    },
    true
  );

  /**
   * The theme replaces every child of <cart-drawer> after a cart change,
   * which drops focus to <body>; the focus trap would then send the next Tab
   * to the close button. Remember what was activated and put focus on its
   * counterpart in the fresh DOM.
   */
  function rememberFocus() {
    var active = document.activeElement;
    if (!keyboardUser || !drawer || !active || !drawer.contains(active)) return;

    var item = active.closest(SEL.item);
    pendingFocus = {
      key: item && item.getAttribute('data-cvd-line-key'),
      sel:
        ['.cvd-qty__btn--plus', '.cvd-qty__btn--minus', '.cvd-qty__field', '.cvd-item__remove'].filter(function (s) {
          return active.matches(s);
        })[0] || null,
      upsell: !!active.closest('.cvd-upsell-card')
    };
  }

  function restoreFocus() {
    if (!pendingFocus || !drawer) return;

    var p = pendingFocus;
    var target = null;
    // another request is still running: its render needs the target too
    if (!inflight) pendingFocus = null;

    if (p.key) {
      var item = drawer.querySelector('[data-cvd-line-key="' + p.key + '"]');
      target = item
        ? (p.sel && item.querySelector(p.sel + ':not([aria-disabled])')) || item.querySelector('.cvd-item__title')
        : drawer.querySelector('.cvd-item__title') || drawer.querySelector('.cvd-empty__button') || drawer.querySelector('.cvd-head__close');
    } else if (p.upsell) {
      target = drawer.querySelector(SEL.sliderNext);
      if (!target || target.hidden || target.offsetParent === null) target = drawer.querySelector('.cvd-checkout__button');
    }

    if (target) target.focus({ preventScroll: true });
  }

  /* ----------------------------------------------------------- order note */

  function rememberNote(scope) {
    var notes = $(SEL.notes, scope);
    if (!notes) return;
    noteOpen = notes.open;
    var field = notes.querySelector('[name="note"]');
    if (field) noteValue = field.value;
  }

  function restoreNote(scope) {
    var notes = $(SEL.notes, scope);
    if (!notes) return;
    if (noteOpen !== null) notes.open = noteOpen;
    var field = notes.querySelector('[name="note"]');
    if (field && noteValue !== null && field.value !== noteValue) field.value = noteValue;
  }

  /* ------------------------------------------------------------------ geo */

  function readCachedCountry() {
    try {
      return window.sessionStorage.getItem(GEO_CACHE_KEY);
    } catch (error) {
      return null;
    }
  }

  function cacheCountry(code) {
    try {
      window.sessionStorage.setItem(GEO_CACHE_KEY, code);
    } catch (error) {
      /* private mode or full storage: one lookup per page instead */
    }
  }

  function pickCountry(payload) {
    var detected = payload && payload.detected_values;
    var country = detected && detected.country;

    if (typeof country === 'string') return country;
    if (!country) return '';

    return country.handle || country.iso_code || country.code || '';
  }

  function resolveCountry() {
    var cached = readCachedCountry();
    if (cached) return Promise.resolve(cached);
    // a failed lookup is remembered for this page only, not the whole session
    if (geoFailed) return Promise.resolve('');
    if (geoRequest) return geoRequest;

    geoRequest = fetch(GEO_URL, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('geo ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        return String(pickCountry(payload) || '').toUpperCase();
      })
      .catch(function () {
        return '';
      })
      .then(function (code) {
        geoRequest = null;
        if (code) cacheCountry(code);
        else geoFailed = true;
        return code;
      });

    return geoRequest;
  }

  function applyGeo(scope) {
    var nodes = $$(SEL.geo, scope);
    if (!nodes.length) return;

    resolveCountry().then(function (code) {
      nodes.forEach(function (node) {
        var allow = (node.getAttribute('data-cvd-geo-allow') || '').toUpperCase();
        node.hidden = !code || allow.indexOf(',' + code + ',') === -1;
      });
    });
  }

  /* ------------------------------------------------------------- carousel */

  function Slider(root) {
    this.root = root;
    this.viewport = $(SEL.sliderViewport, root);
    this.track = $(SEL.sliderTrack, root);
    this.prev = $(SEL.sliderPrev, root);
    this.next = $(SEL.sliderNext, root);
    this.dots = $(SEL.sliderDots, root);
    this.index = 0;
    this.slides = [];
    this.rtl = isRtl(root);

    this.onPrev = this.go.bind(this, -1);
    this.onNext = this.go.bind(this, 1);
    this.onDotClick = this.handleDotClick.bind(this);
    this.onPointerDown = this.handlePointerDown.bind(this);
    this.onPointerMove = this.handlePointerMove.bind(this);
    this.onPointerUp = this.handlePointerUp.bind(this);
    this.onClick = this.handleClick.bind(this);

    if (this.prev) this.prev.addEventListener('click', this.onPrev);
    if (this.next) this.next.addEventListener('click', this.onNext);
    if (this.dots) this.dots.addEventListener('click', this.onDotClick);

    if (this.viewport) {
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      // capture phase: runs before a link's own click, so a swipe that ends
      // on the image or title never follows it
      this.viewport.addEventListener('click', this.onClick, true);
      this.viewport.addEventListener('dragstart', function (event) {
        event.preventDefault();
      });
      // focus landing in a clipped slide must never scroll the viewport
      this.viewport.addEventListener(
        'focusin',
        function () {
          this.viewport.scrollLeft = 0;
        }.bind(this)
      );
    }
  }

  Slider.prototype.setSlides = function (nodes, index) {
    this.track.innerHTML = '';
    nodes.forEach(
      function (node) {
        this.track.appendChild(node);
      }.bind(this)
    );

    this.slides = $$(SEL.slide, this.track);
    this.index = Math.min(Math.max(index || 0, 0), Math.max(this.slides.length - 1, 0));
    this.renderDots();
    this.update(false);

    var single = this.slides.length < 2;
    if (this.prev) this.prev.hidden = single;
    if (this.next) this.next.hidden = single;
    if (this.dots) this.dots.hidden = single;

    return this.slides.length;
  };

  Slider.prototype.renderDots = function () {
    if (!this.dots) return;

    this.dots.innerHTML = '';

    this.slides.forEach(
      function (slide, index) {
        var item = document.createElement('li');
        var button = document.createElement('button');
        var title = slide.querySelector('.cvd-upsell-card__title');

        button.type = 'button';
        button.className = 'cvd-slider__dot';
        button.setAttribute('data-cvd-slider-dot', index);
        button.setAttribute('aria-label', index + 1 + '/' + this.slides.length + (title ? ' — ' + title.textContent.trim() : ''));
        item.appendChild(button);
        this.dots.appendChild(item);
      }.bind(this)
    );
  };

  Slider.prototype.handleDotClick = function (event) {
    var button = event.target.closest('[data-cvd-slider-dot]');
    if (!button) return;

    this.index = parseInt(button.getAttribute('data-cvd-slider-dot'), 10) || 0;
    this.update(true);
  };

  Slider.prototype.go = function (direction) {
    if (this.slides.length < 2) return;

    this.index = (this.index + direction + this.slides.length) % this.slides.length;
    this.update(true);
  };

  Slider.prototype.offset = function (extraPx) {
    // the theme's logical flip mirrors the travel for right-to-left storefronts
    var flip = this.rtl ? -1 : 1;
    var base = -this.index * 100 * flip;
    return extraPx ? 'translateX(calc(' + base + '% + ' + extraPx + 'px))' : 'translateX(' + base + '%)';
  };

  Slider.prototype.update = function (animate) {
    if (!this.track) return;

    this.track.classList.toggle('is-dragging', !animate);
    this.track.style.transform = this.offset(0);

    if (!animate) {
      this.track.offsetHeight;
      this.track.classList.remove('is-dragging');
    }

    if (this.dots) {
      $$('li', this.dots).forEach(
        function (item, index) {
          var active = index === this.index;
          item.classList.toggle('is-active', active);
          var button = item.firstElementChild;
          if (button) {
            if (active) button.setAttribute('aria-current', 'true');
            else button.removeAttribute('aria-current');
          }
        }.bind(this)
      );
    }

    // off-screen slides are neither read nor reachable with the keyboard
    this.slides.forEach(
      function (slide, index) {
        var active = index === this.index;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (active) slide.removeAttribute('inert');
        else slide.setAttribute('inert', '');
      }.bind(this)
    );

    if (this.viewport) this.viewport.scrollLeft = 0;

    sliderIndex = this.index;
  };

  Slider.prototype.handlePointerDown = function (event) {
    // a swipe that left no click behind must never swallow this tap
    this.swiped = false;

    if (this.slides.length < 2) return;
    if (event.target.closest('button, select, input')) return;

    this.dragStartX = event.clientX;
    this.dragging = true;

    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
  };

  Slider.prototype.handlePointerMove = function (event) {
    if (!this.dragging) return;

    var delta = event.clientX - this.dragStartX;

    this.track.classList.add('is-dragging');
    this.track.style.transform = this.offset(delta * 0.6);
  };

  Slider.prototype.handlePointerUp = function (event) {
    if (!this.dragging) return;

    this.dragging = false;
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    this.track.classList.remove('is-dragging');

    // the browser took the gesture (vertical scroll, pinch): snap back
    if (event.type === 'pointercancel') {
      this.update(true);
      return;
    }

    var delta = event.clientX - this.dragStartX;

    if (Math.abs(delta) > 40) {
      this.swiped = true;
      this.go((delta < 0) !== this.rtl ? 1 : -1);

      // on touch, and when the mouse is released outside the viewport, no
      // click follows the swipe: drop the flag before the next real tap
      var self = this;
      setTimeout(function () {
        self.swiped = false;
      }, 0);
    } else {
      this.update(true);
    }
  };

  /** Swallows the click a swipe leaves behind so a link under it is not followed. */
  Slider.prototype.handleClick = function (event) {
    if (!this.swiped) return;

    this.swiped = false;
    event.preventDefault();
    event.stopPropagation();
  };

  function bindVariantSelects(scope) {
    $$(SEL.variantSelect, scope).forEach(function (select) {
      select.addEventListener('change', function () {
        var option = select.options[select.selectedIndex];
        if (!option) return;

        var card = select.closest('.cvd-upsell-card');
        if (!card) return;

        var price = $(SEL.upsellPrice, card);
        var compare = $(SEL.upsellCompare, card);
        var comparePrice = option.getAttribute('data-compare') || '';

        if (price) price.textContent = option.getAttribute('data-price') || '';
        if (compare) {
          compare.textContent = comparePrice;
          compare.hidden = comparePrice === '';
        }
      });
    });
  }

  /** Slides from cached markup, minus anything that is now in the cart. */
  function slidesFromMarkup(markup, state) {
    var holder = document.createElement('div');
    holder.innerHTML = markup;

    var container = $(SEL.slides, holder);
    var slides = container ? $$(SEL.slide, container) : [];

    return slides.filter(function (slide) {
      var id = slide.getAttribute('data-cvd-product-id');
      return !id || state.products.indexOf(id) === -1;
    });
  }

  function paintUpsells(box, state) {
    if (!upsellCache) return 0;

    var nodes = slidesFromMarkup(upsellCache, state);
    if (!nodes.length) {
      box.hidden = true;
      return 0;
    }

    slider.setSlides(nodes, sliderIndex);
    bindVariantSelects(slider.track);
    box.hidden = false;
    return nodes.length;
  }

  /** Cache key: which products (and which intent) the cached slides answer. */
  function seedKey(state) {
    return state.seeds.join(',') + '|' + state.intent + '|' + state.fallback;
  }

  /**
   * The recommendations are fetched once the drawer has been opened, and
   * again only when the set of cart products changed (a new seed), when a
   * product was removed (the cached markup no longer carries it) or when the
   * cache has run down to a single slide. Everything else repaints from the
   * cache.
   */
  function needsFetch(state, painted) {
    if (!upsellCache) return true;
    if (seedKey(state) !== upsellSeed) return true;
    if (painted < 2) return true;

    var previous = upsellProducts || [];
    for (var i = 0; i < previous.length; i++) {
      if (state.products.indexOf(previous[i]) === -1) return true;
    }
    return false;
  }

  function recommendationUrl(state, seed, intent) {
    return (
      state.url +
      '?section_id=cart-drawer-upsells' +
      '&product_id=' +
      encodeURIComponent(seed) +
      '&limit=' +
      encodeURIComponent(state.limit) +
      '&intent=' +
      encodeURIComponent(intent)
    );
  }

  function fallbackUrl(state) {
    if (!state.fallback) return '';
    return state.fallback + (state.fallback.indexOf('?') === -1 ? '?' : '&') + 'section_id=cart-drawer-upsells';
  }

  /** One section render → its slides (an array, empty on any failure). */
  function fetchSlides(url) {
    return fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('recommendations ' + response.status);
        return response.text();
      })
      .then(function (markup) {
        var holder = document.createElement('div');
        holder.innerHTML = markup;
        var container = $(SEL.slides, holder);
        return container ? $$(SEL.slide, container) : [];
      })
      .catch(function () {
        return null;
      });
  }

  /**
   * Merges the recommendation lists into one carousel:
   *   1. seeds are the cart's products, newest first: the shopper sees what
   *      goes with the product they just added before the older ones
   *   2. the lists are interleaved (first pick of every seed, then the second
   *      pick of every seed, …) so every cart product gets a say
   *   3. each product appears once, nothing that is in the cart
   *   4. the fallback collection only pads what is left up to the limit
   */
  function mergeSlides(groups, fallbackSlides, state) {
    var seen = {};
    var out = [];

    function push(slide) {
      if (out.length >= state.limit) return;
      var id = slide.getAttribute('data-cvd-product-id');
      if (!id || seen[id] || state.products.indexOf(id) !== -1) return;
      seen[id] = true;
      out.push(slide);
    }

    var longest = 0;
    groups.forEach(function (group) {
      longest = Math.max(longest, group.length);
    });

    for (var round = 0; round < longest; round++) {
      for (var g = 0; g < groups.length; g++) {
        if (groups[g][round]) push(groups[g][round]);
      }
    }

    (fallbackSlides || []).forEach(push);

    return out;
  }

  function syncUpsells(scope, state) {
    var box = $(SEL.upsell, scope);
    if (!box) return;

    if (!state || !state.upsells || !state.count || !state.seeds.length) {
      box.hidden = true;
      upsellSeed = null;
      upsellProducts = null;
      upsellCache = null;
      return;
    }

    var root = $(SEL.slider, box);
    if (!root) return;

    // a fresh render means fresh nodes; the slider is cheap to rebuild
    slider = new Slider(root);

    // repaint from the cache straight away so the carousel never blinks
    var painted = paintUpsells(box, state);

    if (!opened) return;

    var fetchNeeded = needsFetch(state, painted);
    upsellProducts = state.products.slice();
    if (!fetchNeeded) return;
    upsellSeed = seedKey(state);

    var intents = state.intent === 'both' ? ['complementary', 'related'] : [state.intent];

    // one request per seed and intent, all in parallel
    var requests = [];
    state.seeds.forEach(function (seed) {
      intents.forEach(function (intent) {
        requests.push(fetchSlides(recommendationUrl(state, seed, intent)));
      });
    });

    var fallback = fallbackUrl(state);
    if (fallback) requests.push(fetchSlides(fallback));

    var request = ++upsellRequest;

    Promise.all(requests).then(function (results) {
      if (request !== upsellRequest) return;

      var fallbackSlides = fallback ? results.pop() : [];
      var failed = results.every(function (r) {
        return r === null;
      });

      // every recommendation request failed (offline, 5xx): keep what we
      // have and try again on the next cart change
      if (failed && !(fallbackSlides && fallbackSlides.length)) {
        upsellSeed = null;
        if (!upsellCache) box.hidden = true;
        return;
      }

      // one group per seed: its intents in order (complementary first)
      var groups = [];
      var perSeed = intents.length;
      for (var i = 0; i < results.length; i += perSeed) {
        var group = [];
        for (var j = 0; j < perSeed; j++) {
          group = group.concat(results[i + j] || []);
        }
        groups.push(group);
      }

      var merged = mergeSlides(groups, fallbackSlides || [], state);

      var holder = document.createElement('div');
      holder.setAttribute('data-cvd-slides', '');
      merged.forEach(function (slide) {
        holder.appendChild(slide);
      });
      upsellCache = holder.outerHTML;

      // the drawer may have re-rendered while the requests were in flight
      var currentBox = drawer && $(SEL.upsell, drawer);
      var currentState = drawer && readState(drawer);
      if (!currentBox || !currentState) return;

      if (!slider || !currentBox.contains(slider.root)) {
        var currentRoot = $(SEL.slider, currentBox);
        if (!currentRoot) return;
        slider = new Slider(currentRoot);
      }

      paintUpsells(currentBox, currentState);
    });
  }

  /* ----------------------------------------------------------------- init */

  function init(afterRender) {
    if (!drawer || !drawer.isConnected) return;

    var state = readState(drawer);

    syncTiers(drawer);
    syncUpsells(drawer, state);
    restoreNote(drawer);
    applyGeo(drawer);
    restoreFocus();

    if (afterRender) announceRender();
  }

  function scheduleInit() {
    if (itemTimer) {
      clearTimeout(itemTimer);
      itemTimer = null;
    }
    if (initQueued) return;
    initQueued = true;

    Promise.resolve().then(function () {
      initQueued = false;
      init(true);
    });
  }

  function onOpen() {
    var first = !opened;
    opened = true;
    if (first) init(false);
  }

  function bind() {
    var current = $(SEL.drawer);
    if (!current || current === drawer) return;

    if (observer) observer.disconnect();

    drawer = current;
    lastFill = null;
    lastReached = null;
    slider = null;

    // the theme swaps the drawer's children on every cart change
    observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].type === 'attributes') {
          if (drawer.hasAttribute('open')) onOpen();
        } else {
          scheduleInit();
          return;
        }
      }
    });

    observer.observe(drawer, { childList: true, attributes: true, attributeFilter: ['open'] });

    drawer.addEventListener('dialog:before-show', onOpen);

    // the order note is part of what the theme re-renders: keep what was typed
    drawer.addEventListener('toggle', function (event) {
      if (event.target && event.target.matches && event.target.matches(SEL.notes)) noteOpen = event.target.open;
    }, true);
    drawer.addEventListener('input', function (event) {
      if (event.target && event.target.name === 'note') noteValue = event.target.value;
    });

    if (drawer.hasAttribute('open')) opened = true;

    init(false);
  }

  /* --------------------------------------------------------------- events */

  document.addEventListener('theme:loading:start', onLoadingStart);
  document.addEventListener('theme:loading:end', onLoadingEnd);

  // the theme dispatches this on the <line-item> whose quantity is changing
  document.addEventListener('line-item:will-change', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    rememberFocus();
    rememberNote(drawer);

    var item = target.closest(SEL.item);
    if (item) item.classList.add(CLASS_LOADING);
  });

  // upsell forms: caught at submit (capture) because the theme's ProductForm
  // disables the button, which already drops focus, before it reports loading
  document.addEventListener(
    'submit',
    function (event) {
      if (!drawer || !event.target || !drawer.contains(event.target)) return;
      rememberFocus();
      rememberNote(drawer);
    },
    true
  );

  // the theme dispatches this on the product form that failed to add
  document.addEventListener('cart:error', function (event) {
    pendingFocus = null;

    var target = event.target;
    if (!target || !target.closest) return;

    var card = target.closest('.cvd-upsell-card');
    if (!card) return;

    var error = $(SEL.upsellError, card);
    if (!error) return;

    error.textContent = (event.detail && event.detail.error) || '';
    error.hidden = !error.textContent;

    // the theme disabled (and so blurred) the button while adding; give it
    // back to a keyboard user so the next Tab does not restart at the top
    if (keyboardUser) {
      var button = target.querySelector('[type="submit"]');
      if (button) button.focus({ preventScroll: true });
    }
  });

  // the theme editor re-renders the section when its settings change
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector && event.target.querySelector(SEL.drawer)) {
      drawer = null;
      bind();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
